import React, { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Lang, PREFIXED_LANGS, HTML_LANG, ROUTE_SLUGS, publicSlug, localizePath } from './i18n/langs';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundaryWrapper } from './components/ErrorBoundary';
import Layout from './components/Layout';
import Home from './pages/Home';
import EmergencyRepairs from './pages/EmergencyRepairs';
import ElectricalInstallations from './pages/ElectricalInstallations';
import SafetyInspections from './pages/SafetyInspections';
import Maintenance from './pages/Maintenance';
import CIE from './pages/CIE';
import BRIE from './pages/BRIE';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Login from './pages/admin/Login';
import BlogPosts from './pages/admin/BlogPosts';
import EditBlogPost from './pages/admin/EditBlogPost';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Security from './pages/Security';
import NotFound from './pages/NotFound';
import { initGA, logPageView } from './lib/analytics';

// Sincroniza el idioma con la URL (/ = es, /ca, /en, /fr)
const LangRoute: React.FC<{ lang: Lang }> = ({ lang }) => {
  const { i18n } = useTranslation();
  useLayoutEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    document.documentElement.lang = HTML_LANG[lang];
  }, [lang, i18n]);
  return <Layout />;
};

// Slug público de una página en un idioma, sin barra inicial (para <Route path>)
const slug = (key: string, lang: Lang) => publicSlug(key, lang).slice(1);

const renderPublicRoutes = (lang: Lang) => (
  <>
    <Route index element={<Home />} />
    <Route path={slug('/emergency-repairs', lang)} element={<EmergencyRepairs />} />
    <Route path={slug('/electrical-installations', lang)} element={<ElectricalInstallations />} />
    <Route path={slug('/safety-inspections', lang)} element={<SafetyInspections />} />
    <Route path={slug('/maintenance', lang)} element={<Maintenance />} />
    <Route path={slug('/cie', lang)} element={<CIE />} />
    <Route path={slug('/brie', lang)} element={<BRIE />} />
    <Route path="blog" element={<Blog />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path={slug('/privacy-policy', lang)} element={<PrivacyPolicy />} />
    <Route path={slug('/terms', lang)} element={<TermsOfService />} />
    <Route path={slug('/security', lang)} element={<Security />} />
    {/* URLs antiguas (en inglés) -> URL nueva del idioma */}
    {Object.keys(ROUTE_SLUGS)
      .filter((key) => publicSlug(key, lang) !== key)
      .map((key) => (
        <Route key={'old-' + key} path={key.slice(1)} element={<Navigate to={localizePath(key, lang) + '/'} replace />} />
      ))}
    <Route path="*" element={<NotFound />} />
  </>
);

function App() {
  const location = useLocation();

  React.useEffect(() => {
    initGA();
  }, []);

  React.useEffect(() => {
    logPageView(location.pathname + location.search);
  }, [location]);

  return (
    <ErrorBoundaryWrapper>
      <Routes>
        <Route path="/" element={<LangRoute lang="es" />}>
          {renderPublicRoutes('es')}
        </Route>
        {PREFIXED_LANGS.map((lng) => (
          <Route key={lng} path={'/' + lng} element={<LangRoute lang={lng} />}>
            {renderPublicRoutes(lng)}
          </Route>
        ))}
        <Route path="/admin">
          <Route path="login" element={<Login />} />
          <Route path="posts" element={<BlogPosts />} />
          <Route path="posts/new" element={<EditBlogPost />} />
          <Route path="posts/:id/edit" element={<EditBlogPost />} />
        </Route>
      </Routes>
    </ErrorBoundaryWrapper>
  );
}

export default App;